import React, { useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "../../redux/store";
import {
    Loader2,
    Clock,
    Clock3,
    CalendarDays,
    Building,
    MapPin,
    Tag,
    ChevronLeft,
    ClipboardList,
    NotebookPen,
} from "lucide-react";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout";
import { DateTimeUtil } from "../../utils";
import { NgwAvatar } from "../../components/common";
import { ApprovalHistory } from "../../components/notificationv2";
import { callPhpWorkflow } from "../../redux/features/common";
import { getNotificationDetail } from "../../redux/features/notifications";

const EmployeeTimecardRequestNotificationDetail: React.FC = () => {
    const navigate = useNavigate();
    const notificationDetail = useSelector((state: RootState) => state.notification.notificationDetail);
    const user = useSelector((state: RootState) => state.user.user);
    const isLoading = useSelector((state: RootState) => state.notification.isNotificationDetailLoading);
    const dispatch = useAppDispatch();
    const [notes, setNotes] = useState("");
    const isCallingWorkflow = useSelector((state: RootState) => state.common.isCallingWorkflow);

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Timecard Request"));
    }, [dispatch]);

    // Extract timecard request details from notification
    const timecardDetails = (notificationDetail as any)?.timecard_request_details;
    const requestor = timecardDetails?.requestor_detail;
    const notificationDateTime = (notificationDetail as any)?.notification_date_time;
    const formattedRequestDate = timecardDetails?.os_emp_schedule_date
        ? DateTimeUtil.formatWithDayjs(timecardDetails.os_emp_schedule_date, "MMM D, YYYY")
        : null;
    const formattedRequestTimeRange =
        timecardDetails?.start_datetime && timecardDetails?.end_datetime
            ? `${DateTimeUtil.FormatWithDayjsUtc(timecardDetails.start_datetime, "h:mm A")} - ${DateTimeUtil.FormatWithDayjsUtc(
                  timecardDetails.end_datetime,
                  "h:mm A"
              )}`
            : null;
    const submittedAt = notificationDateTime
        ? DateTimeUtil.formatWithDayjs(
              DateTimeUtil.convertUtcToLocalTime(notificationDateTime),
              "MMM D, h:mm A"
          )
        : null;
    const formattedHours = (() => {
        const rawHours = Number(timecardDetails?.hours);
        if (Number.isNaN(rawHours)) return null;

        const wholeHours = Math.floor(rawHours);
        const minutes = Math.round((rawHours - wholeHours) * 60);
        return `${wholeHours}h ${minutes.toString().padStart(2, "0")}m`;
    })();
    const additionalDetails = [
        {
            label: "Department",
            value: timecardDetails?.department?.department_name,
            icon: Building,
        },
        {
            label: "Site",
            value: timecardDetails?.site?.site_name,
            icon: MapPin,
        },
        {
            label: "Earning Code",
            value: timecardDetails?.earning_code?.name,
            icon: Tag,
        },
    ].filter((item) => item.value);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-40 w-full">
                <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
            </div>
        );
    }
    const getCurrentStep = () => {
        const currentStepCode = notificationDetail?.workflow_current_step_details?.step_code;
        return notificationDetail?.workflow_details?.filter((wf) => wf.step_code === currentStepCode)?.[0];
    };

    const handleApprove = async () => {
        const timecard_request_details = (notificationDetail as any)?.timecard_request_details;
        if (!timecard_request_details) return;

        const timecardRequestId = timecard_request_details?.os_employee_timesheet_requests_id;
        const employeeId = requestor?.employee_id;

        if (!timecardRequestId || !employeeId) return;

        const body = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 1,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: timecardRequestId,
            result: { APPROVED: { [employeeId]: [timecardRequestId] }, REJECTED: {} },
            notes,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
        };

        await dispatch(callPhpWorkflow(body));
        refreshData();
    };

    const handleReject = async () => {
        const timecard_request_details = (notificationDetail as any)?.timecard_request_details;
        if (!timecard_request_details) return;

        const timecardRequestId = timecard_request_details?.os_employee_timesheet_requests_id;
        const employeeId = requestor?.employee_id;

        if (!timecardRequestId || !employeeId) return;

        const body = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 0,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: timecardRequestId,
            result: { APPROVED: {}, REJECTED: { [employeeId]: [timecardRequestId] } },
            notes,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
        };

        await dispatch(callPhpWorkflow(body));
        refreshData();
    };

    const refreshData = async () => {
        await dispatch(
            getNotificationDetail({
                notificationId: notificationDetail?.notification_id!,
                employeeId: Number(user.os_emp_account_id!),
            })
        );
    };
    const currentStep = getCurrentStep();
    const canTakeAction =
        currentStep?.current_status === "0" &&
        currentStep?.actor_json?.[user.id!] &&
        notificationDetail?.only_notify_no_action !== "1";

    return (
        <div className="bg-gray-50 min-h-screen pb-8">
            <div className="w-full md:w-1/2 mx-auto">

                {/* Employee Info Card */}
                <div className="mx-4 mt-3">
                    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 border-l-4 border-l-blue-500">
                        <div className="flex items-start gap-3">
                            <div className="w-12 h-12 relative flex-shrink-0">
                                <NgwAvatar src={requestor?.image_url} name={requestor?.emp_name} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] font-semibold tracking-wide text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                        REQUESTOR
                                    </span>
                                </div>
                                <h2 className="text-lg font-semibold text-gray-900 leading-tight">
                                    {requestor?.emp_name || "Employee Name"}
                                </h2>
                                <p className="text-gray-600 text-sm mt-1">Submitted timecard request</p>
                            </div>
                            {submittedAt && (
                                <div className="flex items-start gap-2 text-right text-xs text-gray-500 flex-shrink-0">
                                    <CalendarDays className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-gray-600">Submitted</p>
                                        <p>{submittedAt}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white mx-4 rounded-xl shadow-sm border border-gray-100 p-4 mb-3 mt-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                        <ClipboardList className="w-4 h-4 text-gray-600" />
                        <h3 className="text-sm font-semibold text-gray-900">Request Summary</h3>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-3">
                        <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
                                <CalendarDays className="w-4 h-4 text-blue-600" />
                            </div>
                            <p className="text-[11px] text-gray-500">Date</p>
                            <p className="text-sm font-medium text-gray-900 mt-1 leading-snug break-words">
                                {formattedRequestDate || "[Request Date]"}
                            </p>
                        </div>

                        <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
                                <Clock3 className="w-4 h-4 text-blue-600" />
                            </div>
                            <p className="text-[11px] text-gray-500">Time</p>
                            <p className="text-sm font-medium text-gray-900 mt-1 leading-snug break-words">
                                {formattedRequestTimeRange || "[Request Time]"}
                            </p>
                        </div>

                        <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
                                <Clock className="w-4 h-4 text-blue-600" />
                            </div>
                            <p className="text-[11px] text-gray-500">Total Hours</p>
                            <p className="text-sm font-medium text-gray-900 mt-1 leading-snug break-words">
                                {formattedHours || (timecardDetails?.hours ? `${timecardDetails.hours}h` : "[Hours]")}
                            </p>
                        </div>
                    </div>

                    {timecardDetails?.earning_code?.name && (
                        <div className="mt-4">
                            <p className="text-[11px] font-semibold text-gray-400 tracking-wide uppercase mb-2">
                                Request Type
                            </p>
                            <div className="inline-flex items-center gap-2 rounded-lg bg-orange-50 text-orange-700 px-3 py-2">
                                <Clock3 className="w-4 h-4" />
                                <span className="text-sm font-medium">{timecardDetails.earning_code.name}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white mx-4 rounded-xl shadow-sm border border-gray-100 p-4 mb-3">
                    <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                        <Building className="w-4 h-4 text-gray-600" />
                        <h3 className="text-sm font-semibold text-gray-900">Additional Details</h3>
                    </div>

                    <div className="divide-y divide-gray-100">
                        {additionalDetails.map(({ label, value, icon: Icon }) => (
                            <div key={label} className="flex items-center justify-between gap-4 py-3">
                                <div className="flex items-center gap-2 text-gray-500 min-w-0">
                                    <Icon className="w-4 h-4 flex-shrink-0" />
                                    <span className="text-sm">{label}</span>
                                </div>
                                <span className="text-sm text-gray-900 text-right">{value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white mx-4 rounded-xl shadow-sm border border-gray-100 p-4 mb-3">
                    <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                        <NotebookPen className="w-4 h-4 text-gray-600" />
                        <h3 className="text-sm font-semibold text-gray-900">Employee Note</h3>
                    </div>

                    <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 text-sm text-gray-700">
                        {timecardDetails?.request_notes || "No note provided."}
                    </div>

                    {timecardDetails?.breaktimes_json && timecardDetails.breaktimes_json !== "[]" && (
                        <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Break Times</h4>
                            <div className="space-y-2">
                                {(() => {
                                    try {
                                        const breakTimes = JSON.parse(timecardDetails.breaktimes_json);
                                        return breakTimes.map((breakTime: any, index: number) => (
                                            <div
                                                key={index}
                                                className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-600"
                                            >
                                                Break {index + 1}: {breakTime.start} - {breakTime.end}
                                            </div>
                                        ));
                                    } catch (e) {
                                        return <div className="text-sm text-gray-500">No break times</div>;
                                    }
                                })()}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white mx-4 rounded-xl shadow-sm border border-gray-100 p-4 mb-3">
                    {/* Approval History Section */}
                    <ApprovalHistory workflowDetails={notificationDetail?.workflow_details} title="Approval History" />
                </div>

                {canTakeAction && (
                    <div className="bg-white mx-4 rounded-xl shadow-sm border border-gray-100 p-4 mb-3">
                        {/* Add note textbox */}
                        <div>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full border-2 rounded-md p-2"
                                placeholder="Add notes"
                            />
                        </div>

                        <div>
                            {isCallingWorkflow ? (
                                <Loader2 className="animate-spin text-green-500" />
                            ) : (
                                <div className="flex space-x-2 mt-4">
                                    <button
                                        onClick={handleApprove}
                                        className="flex-1 bg-white border border-blue-600 hover:bg-blue-50 text-blue-600 font-medium py-2 px-3 rounded-md transition-colors text-sm"
                                    >
                                        Approve
                                    </button>
                                    <button
                                        onClick={handleReject}
                                        className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-3 rounded-md transition-colors text-sm"
                                    >
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmployeeTimecardRequestNotificationDetail;
